import { Box, Divider, Stack, Typography } from "@mui/material";
import { GradeBox } from "./GradeBox";
import { formatDateRange } from "../../utils/formatDateRange";
import { FileIcon } from "../images";
import { MatrixViewBlock } from "./MatrixViewBlock";
import { FeedbackDataBlock } from "./FeedbackDataBlock";
import { FeedbackFilesBlock } from "./FeedbackFilesBlock";
import { FeedbackImages } from "./FeedbackImages";
import { Feedback } from "../../types/apiTypes";
import { useState } from "react";
import { ReuseModal } from "../ui";
import { ImageSlider } from "./ImageSlider";
import { EllipsisTooltip } from "../ui/CustomTooltip";

export const FeedbackItem = ({ item }: { item: Feedback }) => {
  const [imageIdx, setImageIdx] = useState<number | null>(null);

  const {
    full_name,
    email,
    company_name,
    created_at,
    feedback,
    total_files_attached,
    image_previews,
    functionality_suggestion,
    average_score,
    files,
    accuracy_score,
    expectations_score,
    impression_score,
    business_flows_score,
    images,
  } = item;

  return (
    <Stack
      sx={{
        gap: "12px",
        paddingBottom: "16px",
        borderBottom: "1px solid #E5E5E5",
      }}
    >
      <Stack>
        <Stack gap="8px" direction="row" color="custom" alignItems="center">
          <Typography
            variant="subtitle1"
            color="custom.textDarkBlue"
            fontWeight={600}
          >
            {full_name}
          </Typography>

          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "#A9A9A9",
            }}
          />

          <Typography variant="subtitle1" color="#A9A9A9" fontWeight={500}>
            {company_name}
          </Typography>
        </Stack>
        <Typography variant="body2" color="#4C545B">
          {email}
        </Typography>
      </Stack>
      <Stack gap="16px">
        <Stack flexDirection="row" gap="8px">
          <Stack gap="8px" flexDirection="row" alignItems="center">
            <GradeBox size={16} grade={average_score} />
            <Typography variant="body2" color="#4C545B">
              {average_score}
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ flexShrink: 1 }} />
          <Typography variant="body2" color="#4C545B">
            {formatDateRange(created_at, true)}
          </Typography>
          <Divider orientation="vertical" flexItem sx={{ flexShrink: 1 }} />
          <Stack flexDirection="row" gap="6px" alignItems="center">
            <FileIcon width="16px" height="16px" />
            <Typography variant="body2" color="#4C545B">
              {`${total_files_attached} files attached`}
            </Typography>
          </Stack>
        </Stack>
        <MatrixViewBlock
          business_flows_score={business_flows_score}
          impression_score={impression_score}
          expectations_score={expectations_score}
          accuracy_score={accuracy_score}
        />
        <Stack gap="24px">
          <FeedbackDataBlock
            title={"Share your feedback, idea or bug report"}
            text={feedback}
          />
          {functionality_suggestion && (
            <FeedbackDataBlock
              title={
                "What new functionality would improve your workflow the most?"
              }
              text={functionality_suggestion}
            />
          )}
        </Stack>
        <FeedbackImages
          image_previews={image_previews}
          setImageIdx={setImageIdx}
        />
        <FeedbackFilesBlock files={files} />
      </Stack>
      {imageIdx !== null && (
        <ReuseModal
          isOpen={true}
          onClose={() => setImageIdx(null)}
          title={""}
          withBtnBlock={false}
          maxWidth="90%"
          width="100%"
        >
          <Stack flexDirection="row" gap="24px" flexGrow={1}>
            <Stack maxWidth="66%" width="100%">
              <ImageSlider images={images} imageIndex={imageIdx} />
            </Stack>
            <Stack maxWidth="34%" width="100%" gap="16px">
              <Stack gap="12px">
                <Stack gap="4px">
                  <Typography
                    variant="subtitle1"
                    color="custom.textDarkBlue"
                    fontWeight={600}
                  >
                    {full_name}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="#1F2932"
                    sx={{
                      wordBreak: email.length > 50 ? "break-all" : "normal",
                      whiteSpace: "normal",
                    }}
                  >
                    <EllipsisTooltip>{email}</EllipsisTooltip>
                  </Typography>

                  <Typography variant="body1" color="#878787">
                    {company_name}
                  </Typography>
                </Stack>
                <Stack flexDirection="row" gap="8px">
                  <Stack gap="8px" flexDirection="row" alignItems="center">
                    <GradeBox size={16} grade={average_score} />
                    <Typography variant="body2" color="#4C545B">
                      {average_score}
                    </Typography>
                  </Stack>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ flexShrink: 1 }}
                  />
                  <Typography variant="body2" color="#4C545B">
                    {formatDateRange(created_at, true)}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ flexShrink: 1 }}
                  />
                  <Stack flexDirection="row" gap="6px" alignItems="center">
                    <FileIcon width="16px" height="16px" />
                    <Typography variant="body2" color="#4C545B">
                      {`${total_files_attached} files attached`}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              <Stack gap="24px">
                <MatrixViewBlock
                  isModal={true}
                  business_flows_score={business_flows_score}
                  impression_score={impression_score}
                  expectations_score={expectations_score}
                  accuracy_score={accuracy_score}
                />
                <FeedbackDataBlock
                  title={"Share your feedback, idea or bug report"}
                  text={feedback}
                />
                {functionality_suggestion && (
                  <FeedbackDataBlock
                    title={
                      "What new functionality would improve your workflow the most?"
                    }
                    text={functionality_suggestion}
                  />
                )}
                <FeedbackFilesBlock files={files} />
              </Stack>
            </Stack>
          </Stack>
        </ReuseModal>
      )}
    </Stack>
  );
};
